<template>
    <div v-if="question" class="q-wrap">
        <div class="q-input">
            <body-map :levels="levels" @cycle="cycleZone" />

            <div v-if="selectedIds.length > 0" class="body-chips">
                <button v-for="id in selectedIds" :key="id" type="button" class="body-chip" @click="removeZone(id)">
                    <span class="body-chip-lv" :style="badgeStyle(id)">{{ levels[id] }}</span>
                    {{ zoneLabel(id) }}
                    <v-icon size="13" color="error">mdi-close</v-icon>
                </button>
            </div>

            <kimsa-text-field class="mt-4" @change="comment = $event" :value="comment"
                :placeholder="$t('common.load_comment')" force-value dense no-elevation />
        </div>

        <div class="q-actions">
            <v-btn text class="body-none" @click="setNoHurt">{{ $t('questions.bodyHurtsOptions.no_hurt') }}</v-btn>
            <v-btn :block="$vuetify.breakpoint.xsOnly" large depressed color="primary" class="black--text q-next"
                :disabled="selectedIds.length === 0" @click="sendAnswer">
                {{ $t('common.next') }}
            </v-btn>
        </div>
    </div>
</template>

<script>
import KimsaTextField from '../../common/inputs/KimsaTextField.vue'
import BodyMap from '../../common/BodyMap.vue'
import zones from '@/config/body-map-zones.json'

const SECTION_ORDER = ['upper', 'core', 'lower']
const PAIN_COLORS = ['', '#FDE68A', '#FBBF24', '#FB923C', '#F1553B', '#8B1A1A']

export default {
    components: { KimsaTextField, BodyMap },
    props: {
        question: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            levels: {},
            comment: '',
        }
    },
    computed: {
        selectedIds(vm = this) {
            return Object.keys(vm.levels)
        },
        zoneById(vm = this) {
            const index = {}
            for (const zone of zones) index[zone.id] = zone
            return index
        },
        groupedAnswers(vm = this) {
            return SECTION_ORDER
                .map(section => ({
                    section: vm.$t(`questions.bodyMap.sections.${section}`),
                    values: vm.selectedIds
                        .filter(id => vm.zoneById[id]?.section === section)
                        .map(id => `${vm.zoneLabel(id)} (${vm.levels[id]})`),
                }))
                .filter(g => g.values.length > 0)
        },
    },
    methods: {
        cycleZone(id) {
            const vm = this
            const next = ((vm.levels[id] || 0) + 1) % 6
            if (next === 0) vm.$delete(vm.levels, id)
            else vm.$set(vm.levels, id, next)
        },
        removeZone(id) {
            this.$delete(this.levels, id)
        },
        badgeStyle(id) {
            const level = this.levels[id]
            return {
                background: PAIN_COLORS[level],
                color: level >= 4 ? '#fff' : '#4a2f12',
            }
        },
        zoneLabel(id) {
            const vm = this
            const zone = vm.zoneById[id]
            if (!zone) return id
            const part = vm.$t(`questions.bodyMap.parts.${zone.key}`)
            if (!zone.side) return part
            return `${part} ${vm.$t(`questions.bodyMap.sides.${zone.side}`)}`
        },
        setNoHurt() {
            const vm = this
            vm.levels = {}
            vm.$emit('click:next', vm.question, {
                values: [vm.$t('questions.bodyHurtsOptions.none')],
                comment: vm.comment,
            })
        },
        sendAnswer() {
            const vm = this
            vm.$emit('click:next', vm.question, {
                values: vm.groupedAnswers,
                zones: { ...vm.levels },
                comment: vm.comment,
            })
        },
    },
}
</script>

<style scoped>
.q-wrap {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 12px;
    padding-top: 8px;
}

.q-input {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    max-width: 520px;
    width: 100%;
    margin: 0 auto;
}

.body-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
}

.body-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(232, 92, 92, 0.1);
    border: 1px solid rgba(232, 92, 92, 0.3);
    color: var(--v-error-base);
    border-radius: 20px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
}

.body-chip-lv {
    border-radius: 4px;
    padding: 1px 6px;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.3;
}

.q-actions {
    max-width: 520px;
    width: 100%;
    margin: 0 auto;
    padding-bottom: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.body-none {
    text-transform: none;
    letter-spacing: 0;
    color: var(--v-txtSecondary-base);
}

.q-next {
    border-radius: 12px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0;
}

@media (min-width: 600px) {
    .q-actions {
        flex-direction: row;
        align-items: stretch;
        gap: 10px;
    }

    .q-next {
        flex: 1;
    }

    .body-none {
        flex-shrink: 0;
        white-space: nowrap;
    }
}
</style>
